// La tarjeta de `generar_lote_imagenes` — ADR-2026-09-08-el-chat-trabaja-en-lote.
//
// Un lote son N generaciones que salen con UNA confirmación. La tarjeta tiene cuatro
// momentos:
//   • PROPUESTA — la lista de ítems (miniatura del producto, etiqueta, prompt editable,
//     cantidad con +/−, quitar), el formato y un solo Generar con el costo total.
//   • LANZANDO — los requests salen de a 4 (`lanzarEnParalelo`) sin esperar imágenes
//     (`lanzarFreeformImage`, `wait: false`). Un 402 corta lo que falta.
//   • GENERANDO — un solo poll sobre todos los `execution_id`; la grilla se va llenando
//     con `onProgreso`, con recuadros vacíos para lo que falta.
//   • LISTO — la grilla completa, lo que no salió, y "Abrir resultados".
//
// El lote es UNA tanda en `generacionesStore`: se ve en la columna de resultados de
// cualquier modo y en Creaciones, cuenta para el tope de tres en curso, compromete sus
// créditos en el guard de los demás, y el reanudador del shell la retoma tras una
// recarga. Acá además queda `pending_generation` en el input de la tarjeta (como en
// `FreeformGenerateCard`): al remontar se retoma el poll y la tarjeta resuelve sola.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BotonGenerarSinPreguntar } from '../components/BotonGenerarSinPreguntar';
import { useVisorDelLienzo } from '../visorDelLienzo';
import { CalidadImagenSheet } from '../components/CalidadImagenSheet';
import { labelResolucion, normalizarResolucion } from '../../../lib/resolucionImagen';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Minus, Package, Plus, Sparkles, X } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import CachedImg from '../../../components/CachedImg';
import {
  preloadGeneratedImages,
  type PreloadedImage,
} from '../../../utils/preloadGeneratedImages';
import { sonarListo } from '../../../utils/sonarListo';
import { useAgentEnv } from '../agentEnvContext';
import { useProveedorDeImagen } from '../proveedorDeImagenContext';
import { fetchBrandContext } from '../agentMemoryApi';
import { FormatPicker } from '../FormatPicker';
import {
  creditosComprometidos,
  generacionesStore,
  MAX_TANDAS_EN_CURSO,
  puedeAgregar,
} from '../generacionesEnCurso';
import { getFormatLabel } from '../mockData';
import { useUserModels } from '../modelsContext';
import { fotoDeModeloParaReferencia } from '../modelsApi';
import { conReferenciasFrescas } from '../../../utils/firmasFrescas';
import { ratioDelFormatoElegido } from '../modes/promptLibre/promptLibre';
import { RecuadroGenerando } from '../components/PanelTandaCreaciones';
import {
  pollGeneratedByExecutionId,
  type PolledGeneratedRow,
} from '../pollGeneratedByExecutionId';
import { getProductFullImageUrl } from '../productsApi';
import { useUserProducts } from '../productsContext';
import {
  etiquetarAdjuntas,
  priorizarReferencias,
  MENSAJE_MODELO_SIN_FOTO,
  ReferenciaIrrecuperable,
  referenciaDeLogo,
  referenciaDeProducto,
  type ReferenciaEtiquetada,
} from '../referenciasDeGeneracion';
import { adjuntaQueFalta, adjuntoPorId, ultimoLoteAdjuntado } from '../adjuntosDelMensaje';
import { signReferenceImage } from '../agentAttachments';
import type { UseV2ChatReturn, V2ToolCard } from '../useV2Chat';
import { atlasBackendFetch } from '../../../lib/atlasBackend';
import { liberarLanzamiento, reservarLanzamiento } from './lanzamientoUnico';
import { urlDeFotoDelLote } from './subirFotosDelLote';
import { urlDeFondoGuardado, urlDePoseGuardada } from '../referenciasDelCatalogo';
import {
  agruparPorItem,
  CANTIDAD_MAX_ITEM,
  creditosDelLote,
  descripcionDeNiveles,
  ejecucionesLanzadas,
  estadoDeCelda,
  imagenesEsperadas,
  itemAInput,
  ITEMS_PARA_PLEGAR,
  motivoTopeExcedido,
  normalizarItemsLote,
  progresoLote,
  resumenParaAgente,
  totalImagenes,
  type EstadoItem,
  type ItemLote,
  type ReferenciaLote,
  type ResumenLote,
  type PendienteLote,
} from './loteImagenes';
import {
  escribirDiarioDeLotes,
  leerPendienteDeLote,
  pendienteMasCompleto,
} from './diarioDeLotes';

/** Más viejo que esto no se retoma: si salió, está en Creaciones. Un lote de 100 con la
 *  cola llena puede tardar media hora; el lote corre en el servidor, así que retomar
 *  tarde no cuesta nada (ADR-2026-09-13-el-lote-del-agente-se-encola-en-el-backend). */
const EDAD_MAX_PENDIENTE_MS = 60 * 60_000;

/** Tope del poll, proporcional al tamaño: 30 s por imagen entre 10 y 60 minutos. */
const timeoutPollLote = (total: number): number =>
  Math.min(60 * 60_000, Math.max(10 * 60_000, total * 30_000));

type Fase = 'propuesta' | 'lanzando' | 'generando';

const ultimoFormato = (): string | null => {
  try {
    return localStorage.getItem('atlas-v2-last-format');
  } catch {
    return null;
  }
};


const ETIQUETA = 'text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55';

/**
 * Recuadro de la grilla, en la proporción del formato del lote. Tres estados, que decide
 * `estadoDeCelda`: la imagen, el esqueleto de la miniatura que está bajando, o el recuadro
 * "generando" —el MISMO que la columna de resultados del modo Modelo, con la barra sembrada
 * desde que se lanzó el lote (ADR-2026-09-09-las-cards-del-chat-generan-como-el-panel)—.
 *
 * `yaGenerada` es obligatoria a propósito: antes la celda infería "generando" de que no
 * hubiera URL, y al entrar a un chat viejo un lote terminado mostraba N recuadros generando
 * debajo de su propio "Listo" (ADR-2026-09-14-el-lote-terminado-no-dice-generando).
 *
 * `aspect-[..]` no compila en este Tailwind, así que la proporción va inline.
 */
const Celda: React.FC<{
  src?: string;
  /** ¿La imagen ya EXISTE y sólo falta su URL firmada? Ver el encabezado. */
  yaGenerada: boolean;
  onClick?: () => void;
  etiqueta?: string;
  ratio: number;
  /** Cuándo se lanzó el lote (siembra la barra). Solo hace falta cuando genera. */
  creadaEn?: number;
}> = ({ src, yaGenerada, onClick, etiqueta, ratio, creadaEn }) => {
  // Con URL, la imagen. El `if` va primero —y no un `switch` sobre el estado— porque es lo
  // que le dice a TypeScript que acá abajo `src` existe.
  if (src) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={etiqueta}
        className="relative w-full overflow-hidden rounded-xl bg-neutral-100 transition-transform enabled:active:scale-[0.97]"
        style={{ aspectRatio: String(ratio) }}
      >
        <CachedImg src={src} alt={etiqueta || 'Imagen generada'} className="h-full w-full object-cover" />
      </button>
    );
  }
  if (estadoDeCelda(src, yaGenerada) === 'generando') {
    return <RecuadroGenerando ratio={ratio} creadaEn={creadaEn ?? Date.now()} />;
  }
  // Miniatura en camino: neutro y callado, como el de `GenerationResultView` para este
  // mismo caso. La imagen está; lo único que falta es la firma. Reserva el lugar en la
  // proporción del lote para que la grilla no salte cuando llegue.
  return (
    <div
      className="w-full animate-pulse overflow-hidden rounded-xl bg-neutral-100"
      style={{ aspectRatio: String(ratio) }}
    />
  );
};

export const LoteImagenesCard: React.FC<{
  card: V2ToolCard;
  active: boolean;
  chat: UseV2ChatReturn;
}> = ({ card, active, chat }) => {
  const navigate = useNavigate();
  const visorDelLienzo = useVisorDelLienzo();
  const { userId, companyId } = useAgentEnv();
  const productos = useUserProducts();
  const modelos = useUserModels();
  // La tuerca del Chat (cuentas dev): fuerza el proveedor de imagen de todo el lote.
  const proveedorForzado = useProveedorDeImagen();

  const input = (card.input ?? {}) as {
    titulo?: string;
    format?: string;
    items?: unknown;
    /** El agente todavía está sumando sets (ADR-2026-09-13-el-lote-del-agente-se-encola-
     *  en-el-backend): sin botón hasta que lo cierre — salvo que su turno haya muerto. */
    abierto?: boolean;
    pending_generation?: PendienteLote | null;
  };
  // "Armando" de verdad sólo mientras el agente sigue trabajando: si el turno murió con el
  // lote abierto, se muestra lo que hay con "Generar lo que hay".
  const armando = input.abierto === true && chat.streaming;

  const nombreDeProducto = useCallback(
    (id: string) => productos.find((p) => String(p.id) === id)?.name,
    [productos],
  );

  // Los ítems son estado: el usuario los edita en la tarjeta y cada cambio se persiste
  // en el input (mismo shape que el de la herramienta, así una recarga los lee igual).
  const [items, setItems] = useState<ItemLote[]>(
    () => normalizarItemsLote(input.items, nombreDeProducto).items,
  );
  const itemsRef = useRef(items);
  itemsRef.current = items;
  // El input vigente de la tarjeta, para los patches que corren minutos después de que
  // se armó el closure (el poll): sin esto un patch tardío pisaría las ediciones que se
  // persistieron mientras tanto.
  const inputRef = useRef(card.input);
  inputRef.current = card.input;
  // Mientras el lote está abierto, los sets que el agente suma llegan por el input de la
  // card: se releen para que la lista crezca en pantalla
  // (ADR-2026-09-13-el-lote-del-agente-se-encola-en-el-backend).
  const [verItems, setVerItems] = useState(false);
  // El catálogo puede llegar DESPUÉS de montar: las etiquetas por defecto ("Imagen N")
  // de ítems con producto toman el nombre real cuando aparece.
  useEffect(() => {
    if (productos.length === 0) return;
    setItems((prev) => {
      let cambio = false;
      const next = prev.map((it) => {
        if (!it.productId || !/^Imagen \d+$/.test(it.etiqueta)) return it;
        const nombre = nombreDeProducto(it.productId);
        if (!nombre) return it;
        cambio = true;
        return { ...it, etiqueta: nombre };
      });
      return cambio ? next : prev;
    });
  }, [productos, nombreDeProducto]);

  const [format, setFormat] = useState<string>(input.format || ultimoFormato() || 'post');
  const [editandoFormato, setEditandoFormato] = useState(false);
  const [editandoCalidad, setEditandoCalidad] = useState(false);
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [fase, setFase] = useState<Fase>('propuesta');
  // Se relee cuando cambia la CANTIDAD de ítems del input, en cualquier momento de la
  // propuesta y no sólo mientras `abierto`: la llamada que CIERRA el lote agrega su último
  // set y marca `abierto: false` en el mismo parche, y con el guard de "sólo abierto" la
  // card se quedaba con los ítems del primer set, lanzaba ésos y al lanzar pisaba los del
  // input con los suyos — 15 generadas de 35 pedidas
  // (ADR-2026-09-13-el-lote-del-agente-se-encola-en-el-backend-2). Sólo por cantidad: releer
  // en cada render pisaría ediciones del usuario; un ítem quitado por él también cambia la
  // cantidad, pero el input ya lo refleja, así que releer da lo mismo.
  const cantidadEnInput = Array.isArray(input.items) ? input.items.length : 0;
  useEffect(() => {
    if (fase !== 'propuesta') return;
    setItems(normalizarItemsLote(input.items, nombreDeProducto).items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cantidadEnInput, fase]);
  const [error, setError] = useState<string | null>(null);
  const [estados, setEstados] = useState<EstadoItem[]>([]);
  const [filas, setFilas] = useState<PolledGeneratedRow[]>([]);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const preloadedRef = useRef<Map<string, PreloadedImage>>(new Map());
  const enPreloadRef = useRef<Set<string>>(new Set());
  const disparadoRef = useRef(false);

  const persistirItems = useCallback(
    (next: ItemLote[]) => {
      chat.patchToolCard(card.id, {
        input: { ...card.input, items: next.map(itemAInput) },
      });
    },
    [card.id, card.input, chat],
  );

  const actualizarItem = (id: string, patch: Partial<ItemLote>) => {
    setItems((prev) => {
      const next = prev.map((it) => (it.id === id ? { ...it, ...patch } : it));
      persistirItems(next);
      return next;
    });
  };
  const quitarItem = (id: string) => {
    setItems((prev) => {
      const next = prev.filter((it) => it.id !== id);
      persistirItems(next);
      return next;
    });
  };

  const total = totalImagenes(items);
  // La calidad vive en el input de la card, igual que en las otras dos: el diario de
  // lotes relee el input al reanudar, y con un `useState` el lote retomado salía en
  // otra calidad que la del botón (ADR-2026-09-13-la-calidad-de-imagen-se-elige-en-la-card).
  const calidad = normalizarResolucion(
    (card.input as { image_size?: string }).image_size ??
      chat.preferenciasDeGeneracion().calidadImagen,
  );
  const creditos = creditosDelLote(items, calidad);

  // ── Miniaturas de lo que va llegando ────────────────────────────────────
  const precargar = useCallback((ids: string[], cid: string) => {
    const nuevas = ids.filter((id) => !enPreloadRef.current.has(id));
    if (nuevas.length === 0) return;
    nuevas.forEach((id) => enPreloadRef.current.add(id));
    preloadGeneratedImages(nuevas, cid)
      .then((imgs) => {
        const patch: Record<string, string> = {};
        for (const img of imgs) {
          preloadedRef.current.set(String(img.id), img);
          patch[String(img.id)] = img.optSignedUrl || img.signedUrl;
        }
        setThumbs((prev) => ({ ...prev, ...patch }));
      })
      .catch(() => {
        // Sin miniatura la celda queda en esqueleto; la fila está igual en Creaciones.
        nuevas.forEach((id) => enPreloadRef.current.delete(id));
      });
  }, []);

  // ── Referencias de un ítem → URLs firmadas (como FreeformGenerateCard) ───
  // Etiquetadas: si el ítem se pasa del tope, cae primero el fondo y nunca el producto
  // (ADR-2026-09-08-diez-referencias-y-recorte-por-prioridad).
  const resolverReferencias = useCallback(
    async (refs: ReferenciaLote[]): Promise<ReferenciaEtiquetada[]> => {
      const etiquetadas: ReferenciaEtiquetada[] = [];
      // Qué es la imagen, si el agente lo dijo
      // (ADR-2026-09-22-el-agente-etiqueta-lo-que-adjunta-el-usuario).
      const conEtiqueta = (r: ReferenciaEtiquetada, spec: ReferenciaLote): ReferenciaEtiquetada =>
        spec.etiqueta ? { ...r, etiqueta: spec.etiqueta } : r;
      let modeloSinFoto = false;
      for (const spec of refs) {
        try {
          if (spec.source === 'producto' && spec.product_id && companyId) {
            const full = await getProductFullImageUrl(companyId, spec.product_id);
            const u = full || productos.find((p) => String(p.id) === spec.product_id)?.imageUrl;
            // Mismo criterio que la card de una sola imagen: un link que no es de nuestro
            // Storage no viaja, viaja el id
            // (ADR-2026-09-21-el-producto-recien-traido-viaja-por-id-y-no-por-link).
            if (u) etiquetadas.push(referenciaDeProducto(u, spec.product_id));
          } else if (spec.source === 'modelo' && spec.model_id) {
            // Re-firmada, no la de la lista (vence a la hora):
            // ADR-2026-10-06-la-foto-del-modelo-se-refirma-en-el-chat.
            const m = modelos.find((mm) => String(mm.id) === String(spec.model_id));
            const u = m ? await fotoDeModeloParaReferencia(m) : null;
            // Sin la foto del modelo elegido el lote no sale: saldría otra persona y se
            // cobraría igual (ADR-2026-10-06-sin-la-foto-del-modelo-no-se-genera).
            if (m && !u) modeloSinFoto = true;
            if (u) etiquetadas.push({ url: u, tipo: 'modelo' });
          } else if (spec.source === 'logo' && userId && companyId) {
            const b = await fetchBrandContext(userId, companyId);
            if (b?.logoUrl) etiquetadas.push(referenciaDeLogo(b.logoUrl));
          } else if (spec.source === 'generada' && spec.image_id && companyId) {
            const imgs = await preloadGeneratedImages([spec.image_id], companyId);
            if (imgs[0]?.signedUrl)
              etiquetadas.push(conEtiqueta({ url: imgs[0].signedUrl, tipo: 'generada' }, spec));
          } else if (spec.source === 'pose' && spec.pose_id && companyId) {
            // Una pose por ÍTEM: es lo que permite una serie con una postura distinta en
            // cada foto (ADR-2026-09-14-la-pose-y-el-fondo-son-referencias-por-id). Antes
            // las poses enganchadas iban a TODOS los ítems por igual.
            const u = await urlDePoseGuardada(spec.pose_id, companyId);
            if (u) etiquetadas.push({ url: u, tipo: 'pose' });
          } else if (spec.source === 'fondo' && spec.fondo_id) {
            const u = await urlDeFondoGuardado(spec.fondo_id, companyId ?? '');
            if (u) etiquetadas.push({ url: u, tipo: 'fondo' });
          } else if (spec.source === 'adjunto' && spec.adjunto_id) {
            // Una imagen subida en un mensaje anterior, por el id del marcador
            // (ADR-2026-09-22-las-imagenes-subidas-se-reusan-por-adjunto-id).
            const a = adjuntoPorId(chat.messages, spec.adjunto_id);
            const u = a ? await signReferenceImage(a.storagePath, a.bucket) : null;
            if (u) etiquetadas.push(conEtiqueta({ url: u, tipo: 'adjunto' }, spec));
          } else if (spec.source === 'adjunto' && spec.foto_id) {
            // UNA foto puntual del montón que adjuntó el usuario. Se sube recién acá:
            // sólo se paga por las que un ítem de verdad usa
            // (ADR-2026-09-08-el-agente-mira-las-fotos-de-a-diez).
            const lote = ultimoLoteAdjuntado(chat.messages);
            const u = await urlDeFotoDelLote(lote?.loteId, spec.foto_id, companyId);
            if (u) etiquetadas.push(conEtiqueta({ url: u, tipo: 'adjunto' }, spec));
          } else if (spec.source === 'adjunto') {
            // Las fotos que el usuario adjuntó al último envío: en un lote entran
            // SÓLO en los ítems que las piden (en generate_image entran solas). Con
            // `etiqueta` (+ `imagen` si son varias), esa adjunta viaja rotulada.
            etiquetadas.push(...etiquetarAdjuntas(chat.getReferenciasEtiquetadas(), [spec]));
          }
        } catch (err) {
          console.warn('[LoteImagenes] referencia no resuelta', spec, err);
        }
      }
      if (modeloSinFoto) throw new ReferenciaIrrecuperable(MENSAJE_MODELO_SIN_FOTO);
      // Con las firmas por vencer renovadas (ADR-2026-10-06-la-foto-del-modelo-se-refirma-en-el-chat).
      return conReferenciasFrescas(priorizarReferencias(etiquetadas));
    },
    [companyId, userId, productos, modelos, chat],
  );

  // ── El poll de la tanda entera ──────────────────────────────────────────
  const pollear = useCallback(
    async (p: PendienteLote) => {
      const rows = await pollGeneratedByExecutionId({
        executionId: ejecucionesLanzadas(p.estados),
        companyId: p.company_id,
        expectedCount: p.expected_count,
        timeoutMs: timeoutPollLote(p.expected_count),
        onProgreso: (r) => {
          setFilas(r);
          const ok = r.filter((x) => !x.has_error).map((x) => x.id);
          precargar(ok, p.company_id);
          // Las llegadas parciales salen de la card para que el lienzo del Agente vaya
          // cambiando recuadros por fotos sin esperar al cierre
          // (ADR-2026-09-16-el-lienzo-lee-los-mensajes). Viajan en el input porque es lo
          // que ya se persiste y se restaura; `restaurarToolCards` conserva la clave.
          chat.patchToolCard(card.id, {
            input: { ...(inputRef.current ?? {}), llegadas: ok },
          });
        },
      });
      setFilas(rows);
      const ok = rows.filter((r) => !r.has_error);
      const resumen = resumenParaAgente({
        items: itemsRef.current,
        estados: p.estados,
        filas: rows,
        companyId: p.company_id,
      });
      const tandaViva = generacionesStore.get().some((g) => g.id === p.tanda_id);
      if (ok.length === 0) {
        if (tandaViva) generacionesStore.quitar(p.tanda_id);
        chat.patchToolCard(card.id, {
          input: { ...(inputRef.current ?? {}), pending_generation: null },
        });
        escribirDiarioDeLotes(card.id, null);
        setError(
          resumen.fallidos[0]?.motivo && resumen.fallidos[0].motivo !== 'no devolvió imágenes'
            ? resumen.fallidos[0].motivo
            : 'No se generó ninguna imagen. Probá de nuevo.',
        );
        setFase('propuesta');
        return;
      }
      let preload: PreloadedImage[] = [];
      try {
        preload = await preloadGeneratedImages(
          ok.map((r) => r.id),
          p.company_id,
        );
      } catch {
        /* sin preload la tanda cierra vacía y la grilla las trae */
      }
      for (const img of preload) preloadedRef.current.set(String(img.id), img);
      setThumbs((prev) => ({
        ...prev,
        ...Object.fromEntries(preload.map((i) => [String(i.id), i.optSignedUrl || i.signedUrl])),
      }));
      if (tandaViva) {
        generacionesStore.cerrar(
          p.tanda_id,
          preload.map((i) => ({ id: String(i.id), src: i.optSignedUrl || i.signedUrl })),
        );
      }
      chat.addChatCredits(ok.length);
      sonarListo();
      chat.resolveToolCard(card.id, resumen);
    },
    [card.id, chat, precargar],
  );

  // Retomar tras una recarga: el pendiente vive en el input persistido.
  useEffect(() => {
    // Las dos fuentes, y gana la que llegó más lejos: el input viaja por
    // `patchToolCard` (setState → efecto → sessionStorage → DB con debounce) y puede
    // haber quedado atrás —o no haberse escrito nunca— si la pestaña murió lanzando.
    const p = pendienteMasCompleto(input.pending_generation, leerPendienteDeLote(card.id));
    if (!p || card.status === 'resolved' || disparadoRef.current) return;
    if (Date.now() - (p.started_at || 0) > EDAD_MAX_PENDIENTE_MS) {
      escribirDiarioDeLotes(card.id, null);
      return;
    }
    if (!Array.isArray(p.estados) || ejecucionesLanzadas(p.estados).length === 0) return;
    disparadoRef.current = true;
    setEstados(p.estados);
    setFase('generando');
    void pollear(p);
    // Sólo al montar: el pendiente viene del input persistido.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Generar ─────────────────────────────────────────────────────────────
  const generar = async () => {
    if (!active || fase !== 'propuesta' || disparadoRef.current || armando) return;
    setError(null);
    const tope = motivoTopeExcedido(items);
    if (tope) {
      setError(tope);
      return;
    }
    if (!userId || !companyId) {
      setError('No pudimos identificar tu cuenta. Probá recargar.');
      return;
    }
    // Una referencia "adjunto" que no existe corta el lote entero antes de gastar, como
    // en la card de una imagen (ADR-2026-09-24-la-seleccion-del-lienzo-se-adjunta-al-escribir).
    const falta = adjuntaQueFalta(
      items.flatMap((i) => i.referencias),
      chat.getReferenciasEtiquetadas().filter((r) => r.tipo === 'adjunto').length,
      chat.messages,
    );
    if (falta) {
      setError(falta);
      chat.resolveToolCard(card.id, { lanzado: false, error: falta });
      return;
    }
    if (!puedeAgregar(generacionesStore.get(), companyId)) {
      setError(
        `Ya hay ${MAX_TANDAS_EN_CURSO} generaciones en curso. Esperá a que termine alguna.`,
      );
      return;
    }
    // EL GUARD VA ACÁ, ANTES DEL PRIMER `await` — ADR-2026-09-13-el-lote-se-lanzaba-dos-veces.
    // Estaba después de la consulta de saldo: un segundo `generar()` en esos ~100 ms (el
    // efecto de auto-generar corre dos veces en StrictMode; en producción, el auto-disparo
    // y un click) pasaba el chequeo y acuñaba sus propios execution_id: el mismo lote de
    // tres salía dos veces y se cobraban seis. `reservarLanzamiento` es el mismo cerrojo
    // por id de card a nivel de módulo, por si en esa ventana hay dos instancias.
    if (!reservarLanzamiento(card.id)) return;
    disparadoRef.current = true;
    setFase('lanzando');
    const soltar = () => {
      liberarLanzamiento(card.id);
      disparadoRef.current = false;
      setFase('propuesta');
    };
    // Saldo fresco menos lo que las tandas en curso ya comprometieron.
    try {
      const { data: cp } = await supabase
        .from('company_profiles')
        .select('imagenes_restantes:imagenes_disponibles')
        .eq('id_company', companyId)
        .single();
      const saldo =
        Number((cp as { imagenes_restantes?: number } | null)?.imagenes_restantes ?? 0) -
        creditosComprometidos(generacionesStore.get(), companyId);
      if (saldo < creditos) {
        setError(
          `No te alcanzan los créditos: el lote necesita ${creditos} y tenés ${Math.max(0, saldo)} disponibles.`,
        );
        soltar();
        return;
      }
    } catch {
      /* si no se pudo leer el saldo, el backend lo chequea igual (402) */
    }

    // ── Un solo pedido al backend: el lote entero, con sus dependencias ──
    // (ADR-2026-09-13-el-lote-del-agente-se-encola-en-el-backend). El backend ordena los
    // niveles, lanza con tope de imágenes en vuelo y sigue aunque esta pestaña muera. El
    // `execution_id` de cada ítem es determinista y vuelve en el 202: el poll de siempre.
    // Con su propio catch: si una referencia irrecuperable tira acá (la foto del modelo),
    // la tarjeta tiene que decirlo y soltar el candado; sin esto quedaba en "Mandando el
    // lote…" sin poder reintentar (ADR-2026-10-06-sin-la-foto-del-modelo-no-se-genera).
    let itemsParaBackend: Awaited<ReturnType<typeof armarItemsParaBackend>>;
    try {
      itemsParaBackend = await armarItemsParaBackend();
    } catch (e) {
      setError(
        e instanceof ReferenciaIrrecuperable ? e.message : 'No pude preparar las referencias del lote. Probá de nuevo.',
      );
      soltar();
      return;
    }
    async function armarItemsParaBackend() {
      return Promise.all(
        items.map(async (item) => {
          const propias = item.referencias.filter((r) => r.source === 'item');
          const resueltas = await resolverReferencias(item.referencias.filter((r) => r.source !== 'item'));
          return {
            id: item.id,
            etiqueta: item.etiqueta,
            prompt: item.prompt,
            cantidad: item.cantidad,
            reference_images: [
              ...resueltas.map((r) => ({
                url: r.url,
                tipo: r.tipo,
                ...(r.productId ? { product_id: r.productId } : {}),
                ...(r.etiqueta ? { etiqueta: r.etiqueta } : {}),
              })),
              ...propias.map((r) => ({ source: 'item', item_id: r.item_id })),
            ],
            ...(item.tarea ? { tarea: item.tarea } : {}),
            ...(item.edicion ? { edicion: item.edicion } : {}),
            ...(item.pielExpuesta ? { piel_expuesta: true } : {}),
            ...(item.fondoBlanco ? { fondo_blanco: true } : {}),
            ...(item.luzFondoBlanco ? { fondo_blanco_iluminacion: item.luzFondoBlanco } : {}),
          };
        }),
      );
    }
    let respuesta: { job_id: string; items: Array<{ id: string; execution_id: string }> };
    try {
      // Import dinámico como el resto de `agentGenerate`: estático lo metería en el bundle principal.
      const { formatSlugToAspect } = await import('../agentGenerate');
      const res = await atlasBackendFetch('/v1/agent/lote', {
        items: itemsParaBackend,
        formato: formatSlugToAspect(format),
        image_size: calidad,
        ...(input.titulo ? { titulo: input.titulo } : {}),
        ...(chat.getChatId() ? { agent_chat_id: chat.getChatId() } : {}),
        ...(proveedorForzado ? { provider_override: proveedorForzado } : {}),
      });
      const json = (await res.json().catch(() => ({}))) as {
        job_id?: string;
        items?: Array<{ id: string; execution_id: string }>;
        error?: string;
      };
      if (!res.ok || !json.job_id || !Array.isArray(json.items)) {
        setError(
          res.status === 402
            ? 'No te alcanzan los créditos para este lote.'
            : json.error
              ? `No se pudo lanzar el lote: ${json.error}`
              : 'No se pudo lanzar el lote. Probá de nuevo.',
        );
        soltar();
        return;
      }
      respuesta = { job_id: json.job_id, items: json.items };
    } catch {
      setError('No pude conectarme para lanzar el lote. Probá de nuevo.');
      soltar();
      return;
    }
    const resultados: EstadoItem[] = respuesta.items.map((r) => ({
      itemId: r.id,
      executionId: r.execution_id,
      estado: 'lanzado' as const,
    }));
    setEstados(resultados);
    const lanzados = ejecucionesLanzadas(resultados);
    const tandaId = generacionesStore.agregar({
      companyId,
      total,
      ratio: ratioDelFormatoElegido(format),
      producto: input.titulo || 'Lote del chat',
      modo: 'chat',
      creditos,
      imagenes: [],
      terminada: false,
      creadaEn: Date.now(),
      executionIds: lanzados,
      esVideo: false,
    });
    try {
      localStorage.setItem('atlas-v2-last-format', format);
    } catch {
      /* noop */
    }
    // Lanzado: el cerrojo de módulo ya no hace falta (un remount lee el diario y polea).
    liberarLanzamiento(card.id);
    const pendiente: PendienteLote = {
      company_id: companyId,
      tanda_id: tandaId,
      started_at: Date.now(),
      format,
      estados: resultados,
      expected_count: total,
      lote_job_id: respuesta.job_id,
    };
    chat.patchToolCard(card.id, {
      input: { ...card.input, items: items.map(itemAInput), format, abierto: false, pending_generation: pendiente },
    });
    escribirDiarioDeLotes(card.id, pendiente);
    setFase('generando');
    await pollear(pendiente);
  };

  const abrirResultados = (ids: string[], cid: string) => {
    // Con el lienzo a la vista, en su visor (ADR-2026-09-18-la-foto-del-chat-se-abre-en-el-visor-del-lienzo).
    if (visorDelLienzo && ids.length > 0) {
      visorDelLienzo(String(ids[0]));
      return;
    }
    const preloadedImages = ids
      .map((id) => preloadedRef.current.get(id))
      .filter((x): x is PreloadedImage => !!x);
    navigate('/redes-sociales/imagenes-generadas', {
      state: { imageIds: ids, companyId: cid, preloadedImages },
    });
  };

  const miniaturaDe = (it: ItemLote): string | undefined =>
    it.productId ? productos.find((p) => String(p.id) === it.productId)?.imageUrl : undefined;

  // Auto-generar: si la preferencia del usuario es no confirmar, el lote sale solo
  // apenas la card queda activa (ADR-2026-09-08-permisos-de-generacion-del-chat). El
  // guard de `generar` (`disparadoRef`, puesto ANTES del primer await, más el cerrojo por
  // id de card) evita el doble disparo; los topes y el
  // corte por saldo siguen valiendo igual, así que un lote sin créditos falla como
  // siempre en vez de salir a medias sin avisar.
  //
  // OJO: va ACÁ, antes del primer `return` condicional. Debajo de cualquiera de ellos,
  // el render de una card resuelta correría menos hooks que el anterior y React tira
  // "Rendered fewer hooks than expected".
  const autogenerar = (card.input as { autogenerate?: boolean } | null)?.autogenerate === true;
  // UNA sola vez por card: `soltar()` vuelve la card a `propuesta` después de cualquier
  // fallo (saldo, 402, red, la foto del modelo), y como el efecto depende de `fase`, sin
  // esto volvía a disparar y la card reintentaba sola el mismo fallo, en loop. Tras el
  // primer intento, reintenta el usuario (ADR-2026-10-06-el-lote-se-autodispara-una-sola-vez).
  const autoDisparadoRef = useRef(false);
  useEffect(() => {
    // `detached` = restaurada de un turno muerto (recarga / chat del historial). Ahí
    // NUNCA se dispara sola aunque el input diga que sí: el turno que lo decidió ya no
    // existe y el usuario no pidió nada en esta carga.
    if (card.status !== 'pending' || card.detached) return;
    if (!autogenerar || !active || fase !== 'propuesta' || disparadoRef.current || input.abierto) return;
    if (autoDisparadoRef.current) return;
    autoDisparadoRef.current = true;
    void generar();
    // `generar` se recrea en cada render; el guard de adentro es el que manda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autogenerar, active, fase, card.status, card.detached]);

  // ── RESUELTA: el resultado, desde el response (sobrevive al cambio de instancia) ──
  if (card.status === 'resolved') {
    const r = (card.response ?? {}) as Partial<ResumenLote>;
    const ids = Array.isArray(r.image_ids) ? r.image_ids.map(String) : [];
    const cid = r.company_id || companyId || '';
    return (
      <ResultadoLote
        ids={ids}
        companyId={cid}
        thumbs={thumbs}
        onFaltan={(faltan) => cid && precargar(faltan, cid)}
        resumen={r}
        onAbrir={() => abrirResultados(ids, cid)}
        ratio={ratioDelFormatoElegido(format)}
      />
    );
  }

  // ── GENERANDO: progreso real + grilla que se llena ──────────────────────
  if (fase === 'lanzando' || fase === 'generando') {
    const esperadas =
      fase === 'lanzando' ? total : imagenesEsperadas(items, estados) || total;
    const progreso = progresoLote(filas, esperadas);
    const porItem = agruparPorItem(filas, estados);
    const celdas: Array<{ id: string; etiqueta: string }> = [];
    for (const it of items) for (const id of porItem.get(it.id) ?? []) celdas.push({ id, etiqueta: it.etiqueta });
    for (const id of porItem.get('') ?? []) celdas.push({ id, etiqueta: '' });
    const faltan = Math.max(0, esperadas - progreso.llegadas - progreso.fallidas);
    const noSalieron = estados.filter((e) => e.estado !== 'lanzado');
    // Proporción del formato del lote y reloj de la tanda: todos los recuadros avanzan
    // en sincronía, como en el panel, y remontar la card no reinicia la barra.
    const ratioCeldas = ratioDelFormatoElegido(format);
    const lanzadoEn = input.pending_generation?.started_at ?? Date.now();
    return (
      <div className="p-5">
        <p className={`${ETIQUETA} mb-1`}>
          {fase === 'lanzando' ? 'Mandando el lote…' : 'Generando el lote…'}
        </p>
        <p className="mb-3 text-[14px] leading-snug text-[#02062B]/[0.85]">
          {progreso.llegadas} de {esperadas} imágenes listas. Podés seguir usando el chat; también
          las vas a ver en Creaciones.
        </p>
        <div className="mb-4 h-1.5 w-full overflow-hidden rounded-full bg-neutral-200">
          <div
            className="h-full rounded-full bg-brand-primary transition-[width] duration-500"
            style={{ width: `${progreso.porcentaje}%` }}
          />
        </div>
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {celdas.map((c) => (
            // Estas salen de `filas`: son imágenes que YA llegaron. Si no tienen URL es
            // porque su miniatura está bajando, no porque falten generarse — mostrarles el
            // recuadro azul las contaba dos veces contra el "N de M listas" de arriba.
            <Celda
              key={c.id}
              src={thumbs[c.id]}
              yaGenerada
              etiqueta={c.etiqueta}
              ratio={ratioCeldas}
              creadaEn={lanzadoEn}
            />
          ))}
          {/* Las que de verdad faltan: acá el recuadro "Generando…" es la verdad. */}
          {Array.from({ length: faltan }, (_, i) => (
            <Celda
              key={`falta-${i}`}
              yaGenerada={false}
              ratio={ratioCeldas}
              creadaEn={lanzadoEn}
            />
          ))}
        </div>
        {noSalieron.length > 0 && (
          <ul className="mt-3 space-y-1 text-[12.5px] text-[#C33B36]">
            {noSalieron.map((e) => {
              const it = items.find((x) => x.id === e.itemId);
              return (
                <li key={e.itemId}>
                  No se pudo: {it?.etiqueta ?? e.itemId}
                  {e.error ? ` — ${e.error}` : ''}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  }

  // ── PROPUESTA ───────────────────────────────────────────────────────────
  if (armando) {
    return (
      <div className="p-5">
        <p className={`${ETIQUETA} mb-1`}>Armando el lote…</p>
        {input.titulo && (
          <p className="mb-2 text-[14.5px] leading-snug text-[#02062B]/[0.9]">{input.titulo}</p>
        )}
        <p className="text-[13.5px] text-[#02062B]/[0.7]">
          {items.length} {items.length === 1 ? 'ítem' : 'ítems'} · {total} {total === 1 ? 'imagen' : 'imágenes'} hasta ahora.
          Cuando termine de armarlo vas a ver el botón.
        </p>
      </div>
    );
  }

  const plegado = items.length > ITEMS_PARA_PLEGAR;
  const niveles = descripcionDeNiveles(items);
  return (
    <div className="p-5">
      <p className={`${ETIQUETA} mb-1`}>Lote de {total} {total === 1 ? 'imagen' : 'imágenes'}</p>
      {input.titulo && (
        <p className="mb-3 text-[14.5px] leading-snug text-[#02062B]/[0.9]">{input.titulo}</p>
      )}
      {input.abierto && (
        <p className="mb-3 text-[13px] text-[#B45309]">
          El agente no terminó de armar este lote. Podés generar lo que hay.
        </p>
      )}
      {plegado && (
        <div className="mb-3 rounded-xl bg-[#F0F2F7] px-3 py-2.5 text-[13px] text-[#02062B]/[0.8]">
          <p>
            {items.length} ítems · {total} imágenes{niveles ? ` · ${niveles}` : ''}
          </p>
          <button
            type="button"
            onClick={() => setVerItems((v) => !v)}
            className="mt-1 text-[12.5px] font-medium text-[#1644EB]"
          >
            {verItems ? 'Ocultar los ítems' : `Ver los ${items.length} ítems`}
          </button>
        </div>
      )}

      {(!plegado || verItems) && (
      <ul className="mb-4 divide-y divide-[#02062B]/[0.06]">
        {items.map((it) => {
          const mini = miniaturaDe(it);
          const editando = editandoId === it.id;
          return (
            <li key={it.id} className="py-2.5">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg bg-[#F0F2F7]">
                  {mini ? (
                    <CachedImg src={mini} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-[#02062B]/[0.35]">
                      <Sparkles className="h-4 w-4" />
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13.5px] font-semibold text-brand-black">{it.etiqueta}</p>
                  {editando ? (
                    <textarea
                      autoFocus
                      value={it.prompt}
                      onChange={(e) => actualizarItem(it.id, { prompt: e.target.value })}
                      onBlur={() => setEditandoId(null)}
                      rows={4}
                      disabled={!active}
                      className="mt-1 w-full resize-none rounded-lg border border-[#02062B]/[0.1] bg-white px-2.5 py-2 text-[13px] leading-snug text-[#02062B]/[0.9] outline-none focus:border-[#1644EB]/[0.4]"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => active && setEditandoId(it.id)}
                      disabled={!active}
                      title="Editar el prompt"
                      className="mt-0.5 line-clamp-2 w-full text-left text-[12.5px] leading-snug text-[#02062B]/[0.6] enabled:hover:text-[#02062B]/[0.85]"
                    >
                      {it.prompt}
                    </button>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    aria-label="Una imagen menos"
                    onClick={() => actualizarItem(it.id, { cantidad: Math.max(1, it.cantidad - 1) })}
                    disabled={!active || it.cantidad <= 1}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#F0F2F7] text-[#02062B]/[0.7] transition-colors enabled:hover:bg-[#02062B]/[0.09] disabled:opacity-40"
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <span className="w-5 text-center text-[13px] font-semibold tabular-nums text-brand-black">
                    {it.cantidad}
                  </span>
                  <button
                    type="button"
                    aria-label="Una imagen más"
                    onClick={() =>
                      actualizarItem(it.id, { cantidad: Math.min(CANTIDAD_MAX_ITEM, it.cantidad + 1) })
                    }
                    disabled={!active || it.cantidad >= CANTIDAD_MAX_ITEM}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#F0F2F7] text-[#02062B]/[0.7] transition-colors enabled:hover:bg-[#02062B]/[0.09] disabled:opacity-40"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Quitar ${it.etiqueta}`}
                    onClick={() => quitarItem(it.id)}
                    disabled={!active}
                    className="ml-1 inline-flex h-7 w-7 items-center justify-center rounded-full text-[#02062B]/[0.45] transition-colors enabled:hover:bg-[#02062B]/[0.08] enabled:hover:text-brand-black disabled:opacity-40"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      )}

      <button
        type="button"
        onClick={() => active && setEditandoFormato(true)}
        disabled={!active}
        className="-mx-2 flex w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left transition-colors enabled:hover:bg-neutral-50"
      >
        <span className="w-16 shrink-0 text-[11.5px] font-medium uppercase tracking-wide text-[#3C3C43]/55">
          Formato
        </span>
        <span className="flex-1 text-[13px] font-medium text-[#02062B]/[0.85]">
          {getFormatLabel(format) || format}
        </span>
        <ChevronRight className="h-4 w-4 shrink-0 text-[#3C3C43]/35" />
      </button>

      <button
        type="button"
        onClick={() => active && setEditandoCalidad(true)}
        disabled={!active}
        className="-mx-2 mb-4 flex w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left transition-colors enabled:hover:bg-neutral-50"
      >
        <span className="w-16 shrink-0 text-[11.5px] font-medium uppercase tracking-wide text-[#3C3C43]/55">
          Calidad
        </span>
        <span className="flex-1 text-[13px] font-medium text-[#02062B]/[0.85]">
          {labelResolucion(calidad)}
        </span>
        <ChevronRight className="h-4 w-4 shrink-0 text-[#3C3C43]/35" />
      </button>

      {error && (
        <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-[13px] text-red-700">
          {error}
        </div>
      )}

      <button
        type="button"
        onClick={generar}
        disabled={!active || items.length === 0}
        className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-brand-primary py-3 px-6 text-[15px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-primary-hover disabled:opacity-50"
      >
        <Package className="h-4 w-4" />
        <span>{input.abierto ? 'Generar lo que hay' : `Generar ${total} ${total === 1 ? 'imagen' : 'imágenes'}`}</span>
        <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[12.5px] font-semibold text-brand-black">
          <img src="/images/token_icon.png" alt="" aria-hidden="true" className="h-3.5 w-3.5 object-contain" />
          {creditos}
        </span>
      </button>
      {/* Genera el lote y apaga la confirmación de las imágenes para las próximas. */}
      <BotonGenerarSinPreguntar
        tipo="lote"
        noPreguntarMas={chat.noPreguntarMas}
        onAccion={generar}
        disabled={!active || items.length === 0}
      />
      <p className="mt-2 text-center text-[12px] text-[#02062B]/[0.5]">
        Tarda unos minutos. Podés seguir usando el chat mientras tanto.
      </p>

      {/* Lo que el usuario elige acá queda como su DEFAULT, igual que en las otras dos
          cards de imagen (ADR-2026-09-14-cambiar-el-formato-en-la-card-cambia-el-default). */}
      <CalidadImagenSheet
        open={editandoCalidad}
        seleccionada={calidad}
        onChange={(id) => {
          chat.patchToolCard(card.id, { input: { ...card.input, image_size: id } });
          chat.recordarPreferencia('calidadImagen', id);
        }}
        onClose={() => setEditandoCalidad(false)}
        cantidadDeImagenes={total}
      />
      <FormatPicker
        open={editandoFormato}
        selectedId={format}
        onChange={(id) => {
          setFormat(id);
          chat.patchToolCard(card.id, { input: { ...card.input, format: id } });
          chat.recordarPreferencia('formatoImagen', id);
        }}
        onClose={() => setEditandoFormato(false)}
      />
    </div>
  );
};

/** La vista resuelta: grilla completa + lo que no salió + abrir. */
const ResultadoLote: React.FC<{
  ids: string[];
  companyId: string;
  thumbs: Record<string, string>;
  onFaltan: (ids: string[]) => void;
  resumen: Partial<ResumenLote>;
  onAbrir: () => void;
  /** Proporción del formato del lote: las celdas se dibujan como salieron las fotos. */
  ratio: number;
}> = ({ ids, companyId, thumbs, onFaltan, resumen, onAbrir, ratio }) => {
  // Las miniaturas que esta instancia no tenga (la tarjeta se remontó resuelta) se piden.
  const faltan = useMemo(() => ids.filter((id) => !thumbs[id]), [ids, thumbs]);
  useEffect(() => {
    if (faltan.length > 0 && companyId) onFaltan(faltan);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faltan.length, companyId]);
  const pedidas = resumen.total_pedidas ?? ids.length;
  const etiquetaDe = (id: string): string =>
    resumen.por_item?.find((p) => p.image_ids.includes(id))?.etiqueta ?? '';
  return (
    <div className="p-4">
      <p className={`${ETIQUETA} mb-2`}>Listo</p>
      <p className="mb-3 text-[14px] leading-snug text-[#02062B]/[0.85]">
        {ids.length === pedidas
          ? `Generamos las ${ids.length} imágenes del lote.`
          : `Generamos ${ids.length} de ${pedidas} imágenes.`}
      </p>
      <div className="mb-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
        {ids.map((id) => (
          <Celda
            key={id}
            src={thumbs[id]}
            // El lote TERMINÓ: estos ids son imágenes que existen. Sin esto, entrar a un
            // chat viejo remontaba la card sin miniaturas y dibujaba "Generando…" debajo
            // del "Listo" (ADR-2026-09-14-el-lote-terminado-no-dice-generando).
            yaGenerada
            etiqueta={etiquetaDe(id)}
            onClick={onAbrir}
            ratio={ratio}
          />
        ))}
      </div>
      {resumen.fallidos && resumen.fallidos.length > 0 && (
        <ul className="mb-3 space-y-1 text-[12.5px] text-[#C33B36]">
          {resumen.fallidos.map((f, i) => (
            <li key={`${f.etiqueta}-${i}`}>
              No salió: {f.etiqueta} — {f.motivo}
            </li>
          ))}
        </ul>
      )}
      {ids.length > 0 && (
        <button
          type="button"
          onClick={onAbrir}
          className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-brand-primary py-3 px-6 text-[14.5px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-primary-hover"
        >
          Abrir resultados
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

export default LoteImagenesCard;
